import { SupabaseClient } from '@supabase/supabase-js';
import { BaseRepository } from './base.repository';
import { Project, ProjectInsert, ProjectUpdate } from '../entities/project';
import { PaginationOptions, PaginatedResponse } from '../types/pagination.types';
import { applyPagination } from '../utils/pagination.utils';

export class ProjectRepository extends BaseRepository<Project, ProjectInsert, ProjectUpdate> {
  constructor(supabase: SupabaseClient) {
    super(supabase, 'projects');
  }

  /**
   * Save a Project entity (create or update based on dirty state)
   */
  async save(project: Project): Promise<Project> {
    if (!project.isDirty()) {
      return project; // No changes to save
    }

    const data = project.toData();
    const { id, created_at, created_by_id, ...updateData } = data; // Remove fields that shouldn't be updated

    const { data: saved, error } = await this.supabase
      .from(this.tableName)
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    // Create new entity instance from saved data
    const savedProject = Project.fromData(saved);
    savedProject.markAsClean();
    return savedProject;
  }

  /**
   * Override findById to return Project entity
   */
  async findById(id: string): Promise<Project | null> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // Not found
      throw error;
    }
    
    return data ? Project.fromData(data) : null;
  }

  /**
   * Override create to return Project entity
   */
  async create(data: ProjectInsert): Promise<Project> {
    const { data: created, error } = await this.supabase
      .from(this.tableName)
      .insert(data)
      .select()
      .single();

    if (error) throw error;
    return Project.fromData(created);
  }

  async findByCreator(creatorId: string, options?: PaginationOptions): Promise<Project[] | PaginatedResponse<Project>> {
    const query = this.supabase
      .from(this.tableName)
      .select('*')
      .eq('created_by_id', creatorId);

    const finalOptions = {
      ...options,
      orderBy: options?.orderBy || 'created_at',
      order: options?.order || 'desc' as const
    };

    return applyPagination<any, Project>(query, finalOptions, (d) => Project.fromData(d));
  }

  async findByUser(userId: string, options?: PaginationOptions): Promise<Project[] | PaginatedResponse<Project>> {
    const query = this.supabase
      .from(this.tableName)
      .select('*, project_users!inner(user_id)')
      .eq('project_users.user_id', userId);

    const finalOptions = {
      ...options,
      orderBy: options?.orderBy || 'created_at',
      order: options?.order || 'desc' as const
    };

    return applyPagination(query, finalOptions, ({ project_users, ...project }) => Project.fromData(project));
  }

  async searchByName(searchTerm: string, userId?: string, options?: PaginationOptions): Promise<Project[] | PaginatedResponse<Project>> {
    const finalOptions = {
      ...options,
      orderBy: options?.orderBy || 'created_at',
      order: options?.order || 'desc' as const
    };

    if (userId) {
      // When filtering by user, we need to join with project_users
      const query = this.supabase
        .from(this.tableName)
        .select('*, project_users!inner(user_id)')
        .ilike('name', `%${searchTerm}%`)
        .eq('project_users.user_id', userId);

      return applyPagination(query, finalOptions, ({ project_users, ...project }) => Project.fromData(project));
    } else {
      // Simple search without user filter
      const query = this.supabase
        .from(this.tableName)
        .select('*')
        .ilike('name', `%${searchTerm}%`);

      return applyPagination(query, finalOptions, (d) => Project.fromData(d));
    }
  }

  async getProjectStats(projectId: string): Promise<{
    documentCount: number;
    prdCount: number;
    memberCount: number;
  }> {
    const [documents, prds, members] = await Promise.all([
      this.supabase
        .from('documents')
        .select('id', { count: 'exact', head: true })
        .eq('project_id', projectId),
      this.supabase
        .from('specifications')
        .select('id', { count: 'exact', head: true })
        .eq('project_id', projectId),
      this.supabase
        .from('project_users')
        .select('id', { count: 'exact', head: true })
        .eq('project_id', projectId),
    ]);

    if (documents.error) throw documents.error;
    if (prds.error) throw prds.error;
    if (members.error) throw members.error;

    return {
      documentCount: documents.count || 0,
      prdCount: prds.count || 0,
      memberCount: members.count || 0,
    };
  }

  async findByInviteCode(inviteCode: string): Promise<Project | null> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select('*')
      .eq('invite_code', inviteCode)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data ? Project.fromData(data) : null;
  }

  async isInviteCodeUnique(inviteCode: string): Promise<boolean> {
    const { count, error } = await this.supabase
      .from(this.tableName)
      .select('id', { count: 'exact', head: true })
      .eq('invite_code', inviteCode);

    if (error) throw error;
    return count === 0;
  }
}